import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { DateInput } from './ui/date-input';
import { Label } from './ui/label';
import {
  List, Download, Printer, FileText, Eye, Pencil, Trash2,
  AlertTriangle, Package, Loader2, Calendar
} from 'lucide-react';
import axios from 'axios';
import { formatLBP, formatUSD, getNumberClass, formatDate } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LedgerDialog = ({ account, organizationId, open, onClose, userRole, fyId }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [ledgerData, setLedgerData] = useState(null);
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [viewVoucher, setViewVoucher] = useState(null);
  const [sourceInvoice, setSourceInvoice] = useState(null);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [hasLoaded, setHasLoaded] = useState(false);
  const [currencyView, setCurrencyView] = useState('USD'); // USD or LBP

  const canEdit = userRole === 'super_admin' || userRole === 'admin' || userRole === 'accountant';
  const canDelete = userRole === 'super_admin' || userRole === 'admin';
  const isSuperAdmin = userRole === 'super_admin';

  // Reset state when dialog opens with a new account
  useEffect(() => {
    if (open && account) {
      setLedgerData(null);
      setError(null);
      setHasLoaded(false);
    }
  }, [open, account]);

  const fetchLedger = async () => {
    if (!account || !organizationId) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ organization_id: organizationId, limit: '5000' });
      if (fyId) params.append('fy_id', fyId);
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      const response = await axios.get(
        `${API}/reports/general-ledger/${account.code}?${params.toString()}`
      );
      setLedgerData(response.data);
      setHasLoaded(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load ledger');
    } finally {
      setLoading(false);
    }
  };

  const handleViewVoucher = async (entry) => {
    try {
      const response = await axios.get(`${API}/vouchers/${entry.voucher_id}`);
      setViewVoucher(response.data);
      setSourceInvoice(null);
      const sourceType = entry.source_type || response.data?.source_type || response.data?.source_doc_type;
      const sourceId = entry.source_id || response.data?.source_id || response.data?.source_doc_id;
      if (sourceType && sourceId && sourceType !== 'voucher' && sourceType !== 'excel_import') {
        setLoadingInvoice(true);
        try {
          const invRes = await axios.get(`${API}/invoices/by-source?source_type=${sourceType}&source_id=${sourceId}`);
          setSourceInvoice(invRes.data);
        } catch { /* Source document may not exist */ }
        finally { setLoadingInvoice(false); }
      }
    } catch {
      alert('Failed to load voucher details');
    }
  };

  const handleEditVoucher = (entry) => {
    onClose();
    navigate('/vouchers', { state: { editVoucherId: entry.voucher_id } });
  };

  const handleDeleteVoucher = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/vouchers/${deleteConfirm.voucher_id}`);
      setDeleteConfirm(null);
      fetchLedger();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete voucher');
    } finally {
      setDeleting(false);
    }
  };

  const handleUnpostVoucher = async (voucherId) => {
    if (!window.confirm('Are you sure you want to unpost this voucher? This will reverse all account balance updates.')) return;
    try {
      await axios.post(`${API}/vouchers/${voucherId}/unpost`);
      fetchLedger();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to unpost voucher');
    }
  };

  const handlePrint = () => {
    if (!ledgerData) return;
    const openingUsd = ledgerData.opening_balance?.usd || 0;
    const openingLbp = ledgerData.opening_balance?.lbp || 0;
    const hasOpening = fromDate && (openingUsd !== 0 || openingLbp !== 0);

    const printContent = `
      <html>
        <head>
          <title>Ledger - ${account.code} ${account.name}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; font-size: 12px; }
            .header { text-align: center; margin-bottom: 20px; }
            .header h1 { font-size: 18px; margin: 0; }
            .header h2 { font-size: 14px; margin: 5px 0; color: #666; }
            .period { text-align: center; font-size: 12px; color: #888; margin-bottom: 10px; }
            table { width: 100%; border-collapse: collapse; margin-top: 15px; }
            th, td { border: 1px solid #ddd; padding: 6px; text-align: left; }
            th { background: #f5f5f5; font-weight: bold; }
            .number { text-align: right; font-family: monospace; }
            .opening { background: #f0f9ff; font-weight: bold; }
            .closing { background: #f0fdf4; font-weight: bold; border-top: 2px solid #333; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>KAIROS - Account Ledger</h1>
            <h2>${account.code} - ${account.name}</h2>
            ${account.name_ar ? `<h2 dir="rtl">${account.name_ar}</h2>` : ''}
          </div>
          ${fromDate || toDate ? `<div class="period">Period: ${fromDate || 'Start'} to ${toDate || 'End'}</div>` : ''}
          <table>
            <thead>
              <tr>
                <th>Date</th><th>Voucher</th><th>Description</th>
                <th class="number">Debit (USD)</th><th class="number">Credit (USD)</th>
                <th class="number">Balance (USD)</th><th class="number">Balance (LBP)</th>
              </tr>
            </thead>
            <tbody>
              ${hasOpening ? `<tr class="opening"><td colspan="5">Opening Balance</td><td class="number">${formatUSD(openingUsd)}</td><td class="number">${formatLBP(openingLbp)}</td></tr>` : ''}
              ${ledgerData.entries.map(entry => `
                <tr>
                  <td>${formatDate(entry.date)}</td>
                  <td>${entry.voucher_number}</td>
                  <td>${entry.description}</td>
                  <td class="number">${entry.debit_usd > 0 ? formatUSD(entry.debit_usd) : '-'}</td>
                  <td class="number">${entry.credit_usd > 0 ? formatUSD(entry.credit_usd) : '-'}</td>
                  <td class="number">${formatUSD((fromDate ? openingUsd : 0) + entry.balance_usd)}</td>
                  <td class="number">${formatLBP((fromDate ? openingLbp : 0) + entry.balance_lbp)}</td>
                </tr>
              `).join('')}
              <tr class="closing"><td colspan="5">Closing Balance</td><td class="number">${formatUSD(ledgerData.closing_balance.usd)}</td><td class="number">${formatLBP(ledgerData.closing_balance.lbp)}</td></tr>
            </tbody>
          </table>
        </body>
      </html>
    `;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const handlePdf = () => {
    // Use the same print approach — browser print dialog can save as PDF
    handlePrint();
  };

  const handleExportCSV = () => {
    if (!ledgerData) return;
    const openingUsd = ledgerData.opening_balance?.usd || 0;
    const openingLbp = ledgerData.opening_balance?.lbp || 0;

    const headers = ['Date', 'Voucher', 'Type', 'Description', 'Debit USD', 'Credit USD', 'Balance USD', 'Debit LBP', 'Credit LBP', 'Balance LBP'];
    const rows = [];
    if (fromDate && (openingUsd !== 0 || openingLbp !== 0)) {
      rows.push(['', '', '', '"Opening Balance"', 0, 0, openingUsd, 0, 0, openingLbp]);
    }
    ledgerData.entries.forEach(e => {
      rows.push([
        e.date, e.voucher_number, e.voucher_type, `"${e.description}"`,
        e.debit_usd, e.credit_usd, (fromDate ? openingUsd : 0) + e.balance_usd,
        e.debit_lbp, e.credit_lbp, (fromDate ? openingLbp : 0) + e.balance_lbp
      ]);
    });
    rows.push(['', '', '', '"Closing Balance"', '', '', ledgerData.closing_balance.usd, '', '', ledgerData.closing_balance.lbp]);

    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ledger_${account.code}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const openingUsd = ledgerData?.opening_balance?.usd || 0;
  const openingLbp = ledgerData?.opening_balance?.lbp || 0;
  const hasOpening = fromDate && (openingUsd !== 0 || openingLbp !== 0);

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="ledger-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <List className="w-5 h-5" />
              Account Ledger
            </DialogTitle>
          </DialogHeader>

          {account && (
            <>
              {/* Account Info + Export Buttons */}
              <div className="p-3 bg-muted/30 rounded-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                <div>
                  <span className="font-mono text-primary text-lg">{account.code}</span>
                  <span className="mx-2">-</span>
                  <span className="font-medium">{account.name}</span>
                  {account.name_ar && (
                    <span className="text-muted-foreground ml-2">({account.name_ar})</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={handlePrint} disabled={!ledgerData} data-testid="ledger-print-btn">
                    <Printer className="w-4 h-4 mr-1" /> Print
                  </Button>
                  <Button variant="outline" size="sm" onClick={handlePdf} disabled={!ledgerData} data-testid="ledger-pdf-btn">
                    <FileText className="w-4 h-4 mr-1" /> PDF
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleExportCSV} disabled={!ledgerData} data-testid="ledger-csv-btn">
                    <Download className="w-4 h-4 mr-1" /> CSV
                  </Button>
                </div>
              </div>

              {/* Date Range + Load Button + Currency Toggle */}
              <div className="flex flex-wrap items-end gap-3 py-2">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <Label className="text-sm text-muted-foreground">From:</Label>
                  <DateInput value={fromDate} onChange={setFromDate} className="w-[150px]" data-testid="ledger-from-date" />
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-sm text-muted-foreground">To:</Label>
                  <DateInput value={toDate} onChange={setToDate} className="w-[150px]" data-testid="ledger-to-date" />
                </div>
                <Button onClick={fetchLedger} disabled={loading} data-testid="load-ledger-btn">
                  {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <List className="w-4 h-4 mr-1" />}
                  Load Ledger
                </Button>
                {/* Currency Toggle */}
                <div className="flex items-center rounded-lg border border-border overflow-hidden" data-testid="currency-toggle">
                  <button
                    onClick={() => setCurrencyView('USD')}
                    className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                      currencyView === 'USD' 
                        ? 'bg-primary text-primary-foreground' 
                        : 'bg-background text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    USD
                  </button>
                  <button
                    onClick={() => setCurrencyView('LBP')}
                    className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                      currencyView === 'LBP' 
                        ? 'bg-primary text-primary-foreground' 
                        : 'bg-background text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    LBP
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Content Area */}
          <div className="flex-1 overflow-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : error ? (
              <div className="text-center py-12 text-red-400">
                <p>{error}</p>
                <Button variant="outline" className="mt-4" onClick={fetchLedger}>Retry</Button>
              </div>
            ) : ledgerData ? (
              <>
                {ledgerData.entries.length === 0 && !hasOpening ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <List className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No transactions found for this account</p>
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="data-table text-sm" data-testid="ledger-entries-table">
                        <thead className="sticky top-0 bg-card">
                          <tr>
                            <th>Date</th>
                            <th>Voucher</th>
                            <th>Description</th>
                            {currencyView === 'USD' ? (
                              <>
                                <th className="text-right">Debit (USD)</th>
                                <th className="text-right">Credit (USD)</th>
                                <th className="text-right">Balance (USD)</th>
                                <th className="text-right">Balance (LBP)</th>
                              </>
                            ) : (
                              <>
                                <th className="text-right">Debit (LBP)</th>
                                <th className="text-right">Credit (LBP)</th>
                                <th className="text-right">Balance (LBP)</th>
                                <th className="text-right">Balance (USD)</th>
                              </>
                            )}
                            <th className="text-center">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {/* Opening Balance Row */}
                          {hasOpening && (
                            <tr className="bg-blue-500/5 border-b" data-testid="opening-balance-row">
                              <td colSpan={5} className="font-medium text-sm text-blue-500 px-3 py-2">
                                Opening Balance
                              </td>
                              {currencyView === 'USD' ? (
                                <>
                                  <td className={`text-right font-mono font-bold px-3 py-2 ${getNumberClass(openingUsd)}`}>
                                    {formatUSD(openingUsd)}
                                  </td>
                                  <td className="text-right font-mono font-bold text-muted-foreground px-3 py-2">
                                    {formatLBP(openingLbp)}
                                  </td>
                                </>
                              ) : (
                                <>
                                  <td className={`text-right font-mono font-bold px-3 py-2 ${getNumberClass(openingLbp)}`}>
                                    {formatLBP(openingLbp)}
                                  </td>
                                  <td className="text-right font-mono font-bold text-muted-foreground px-3 py-2">
                                    {formatUSD(openingUsd)}
                                  </td>
                                </>
                              )}
                              <td></td>
                            </tr>
                          )}
                          {ledgerData.entries.map((entry, idx) => {
                            const runBalUsd = (fromDate ? openingUsd : 0) + entry.balance_usd;
                            const runBalLbp = (fromDate ? openingLbp : 0) + entry.balance_lbp;
                            return (
                              <tr key={idx}>
                                <td className="text-muted-foreground">{formatDate(entry.date)}</td>
                                <td>
                                  <span className={`px-1.5 py-0.5 rounded text-xs voucher-${entry.voucher_type.toLowerCase()}`}>
                                    {entry.voucher_number}
                                  </span>
                                </td>
                                <td className="max-w-[280px] whitespace-normal break-words">{entry.description}</td>
                                {currencyView === 'USD' ? (
                                  <>
                                    <td className="text-right font-mono">
                                      {entry.debit_usd > 0 ? <span className="text-red-400">{formatUSD(entry.debit_usd)}</span> : '-'}
                                    </td>
                                    <td className="text-right font-mono">
                                      {entry.credit_usd > 0 ? <span className="text-green-400">{formatUSD(entry.credit_usd)}</span> : '-'}
                                    </td>
                                    <td className={`text-right font-mono ${getNumberClass(runBalUsd)}`}>
                                      {formatUSD(runBalUsd)}
                                    </td>
                                    <td className="text-right font-mono text-muted-foreground">
                                      {formatLBP(runBalLbp)}
                                    </td>
                                  </>
                                ) : (
                                  <>
                                    <td className="text-right font-mono">
                                      {entry.debit_lbp > 0 ? <span className="text-red-400">{formatLBP(entry.debit_lbp)}</span> : '-'}
                                    </td>
                                    <td className="text-right font-mono">
                                      {entry.credit_lbp > 0 ? <span className="text-green-400">{formatLBP(entry.credit_lbp)}</span> : '-'}
                                    </td>
                                    <td className={`text-right font-mono ${getNumberClass(runBalLbp)}`}>
                                      {formatLBP(runBalLbp)}
                                    </td>
                                    <td className="text-right font-mono text-muted-foreground">
                                      {formatUSD(runBalUsd)}
                                    </td>
                                  </>
                                )}
                                <td>
                                  <div className="flex justify-center gap-1">
                                    <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => handleViewVoucher(entry)} title="View">
                                      <Eye className="w-3 h-3" />
                                    </Button>
                                    {canEdit && (
                                      <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => handleEditVoucher(entry)} title="Edit">
                                        <Pencil className="w-3 h-3" />
                                      </Button>
                                    )}
                                    {isSuperAdmin && (
                                      <Button variant="ghost" size="sm" className="h-7 px-2 text-amber-400" onClick={() => handleUnpostVoucher(entry.voucher_id)} title="Unpost">
                                        <AlertTriangle className="w-3 h-3" />
                                      </Button>
                                    )}
                                    {canDelete && (
                                      <Button variant="ghost" size="sm" className="h-7 px-2 text-red-400" onClick={() => setDeleteConfirm(entry)} title="Delete">
                                        <Trash2 className="w-3 h-3" />
                                      </Button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Closing Balance */}
                    <div className="mt-4 p-4 bg-emerald-500/5 rounded-sm border border-emerald-500/20">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-emerald-500">Closing Balance:</span>
                        <div className="flex gap-6">
                          {currencyView === 'USD' ? (
                            <>
                              <div>
                                <span className="text-muted-foreground text-sm mr-2">USD:</span>
                                <span className={`font-mono font-bold ${getNumberClass(ledgerData.closing_balance.usd)}`}>
                                  ${formatUSD(ledgerData.closing_balance.usd)}
                                </span>
                              </div>
                              <div>
                                <span className="text-muted-foreground text-sm mr-2">LBP:</span>
                                <span className="font-mono font-bold">
                                  {formatLBP(ledgerData.closing_balance.lbp)}
                                </span>
                              </div>
                            </>
                          ) : (
                            <>
                              <div>
                                <span className="text-muted-foreground text-sm mr-2">LBP:</span>
                                <span className={`font-mono font-bold ${getNumberClass(ledgerData.closing_balance.lbp)}`}>
                                  {formatLBP(ledgerData.closing_balance.lbp)}
                                </span>
                              </div>
                              <div>
                                <span className="text-muted-foreground text-sm mr-2">USD:</span>
                                <span className="font-mono font-bold">
                                  ${formatUSD(ledgerData.closing_balance.usd)}
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </>
            ) : !hasLoaded ? (
              <div className="text-center py-16 text-muted-foreground">
                <List className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p className="text-base italic">Select a date range and click "Load Ledger"</p>
                <p className="text-sm mt-1 opacity-60">Leave dates empty to load all transactions</p>
              </div>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>

      {/* View Voucher Dialog */}
      <Dialog open={!!viewVoucher} onOpenChange={() => { setViewVoucher(null); setSourceInvoice(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Voucher Details</DialogTitle>
          </DialogHeader>
          {viewVoucher && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-muted/30 rounded">
                <div>
                  <p className="text-xs text-muted-foreground">Voucher #</p>
                  <p className="font-mono font-bold">{viewVoucher.voucher_number}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Type</p>
                  <p className={`inline-block px-2 py-0.5 rounded text-xs voucher-${viewVoucher.voucher_type.toLowerCase()}`}>
                    {viewVoucher.voucher_type}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Date</p>
                  <p>{formatDate(viewVoucher.date)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Status</p>
                  <p className={viewVoucher.is_posted ? 'status-posted' : 'status-draft'}>
                    {viewVoucher.is_posted ? 'Posted' : 'Draft'}
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-muted-foreground">Description</p>
                  <p>{viewVoucher.description}</p>
                </div>
                {viewVoucher.reference && (
                  <div className="col-span-2">
                    <p className="text-xs text-muted-foreground">Reference</p>
                    <p>{viewVoucher.reference}</p>
                  </div>
                )}
              </div>
              <div>
                <h4 className="font-medium mb-2">Journal Lines</h4>
                <table className="data-table text-sm">
                  <thead>
                    <tr>
                      <th>Account</th>
                      <th>Description</th>
                      <th className="text-right">Debit (USD)</th>
                      <th className="text-right">Credit (USD)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewVoucher.lines.map((line, idx) => (
                      <tr key={idx}>
                        <td>
                          <span className="font-mono text-xs">{line.account_code}</span>
                          <span className="mx-1">-</span>
                          <span className="text-muted-foreground">{line.account_name}</span>
                        </td>
                        <td className="text-muted-foreground">{line.description}</td>
                        <td className="text-right font-mono">
                          {line.debit_usd > 0 ? <span className="text-red-400">${formatUSD(line.debit_usd)}</span> : '-'}
                        </td>
                        <td className="text-right font-mono">
                          {line.credit_usd > 0 ? <span className="text-green-400">${formatUSD(line.credit_usd)}</span> : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2">
                      <td colSpan={2} className="font-medium">Total</td>
                      <td className="text-right font-mono font-bold">${formatUSD(viewVoucher.total_debit_usd)}</td>
                      <td className="text-right font-mono font-bold">${formatUSD(viewVoucher.total_credit_usd)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Source Invoice Items */}
              {loadingInvoice && (
                <div className="flex items-center gap-2 text-muted-foreground text-sm p-3">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading invoice items...
                </div>
              )}
              {sourceInvoice && (
                <div data-testid="source-invoice-section">
                  <div className="flex items-center gap-2 mb-2">
                    <Package className="w-4 h-4 text-primary" />
                    <h4 className="font-medium">{sourceInvoice.doc_type_label} Items</h4>
                    <span className="text-xs text-muted-foreground font-mono ml-auto">{sourceInvoice.doc_number}</span>
                  </div>
                  <div className="p-3 bg-muted/20 rounded-md border border-border/50 mb-2">
                    <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
                      {sourceInvoice.account_name && (
                        <div>
                          <span className="text-muted-foreground">{sourceInvoice.account_label}:</span>{' '}
                          <strong>{sourceInvoice.account_name}</strong>
                          {sourceInvoice.account_code && <span className="text-xs text-muted-foreground ml-1">({sourceInvoice.account_code})</span>}
                        </div>
                      )}
                      {sourceInvoice.payment_method && (
                        <div><span className="text-muted-foreground">Payment:</span> <strong className="capitalize">{sourceInvoice.payment_method}</strong></div>
                      )}
                      {sourceInvoice.supplier_invoice_number && (
                        <div><span className="text-muted-foreground">Supplier Inv#:</span> <strong>{sourceInvoice.supplier_invoice_number}</strong></div>
                      )}
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="data-table text-sm" data-testid="source-invoice-items-table">
                      <thead>
                        <tr>
                          <th className="text-left">#</th>
                          <th className="text-left">Item</th>
                          <th className="text-right">Qty</th>
                          <th className="text-right">Price</th>
                          <th className="text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(sourceInvoice.lines || []).map((line, idx) => (
                          <tr key={idx}>
                            <td className="text-muted-foreground">{idx + 1}</td>
                            <td>
                              <span>{line.item_name || line.name || '-'}</span>
                              {line.item_name_ar && <span className="text-muted-foreground text-xs ml-1">/ {line.item_name_ar}</span>}
                              {line.batch_number && <span className="text-xs text-blue-400 ml-2">Batch: {line.batch_number}</span>}
                            </td>
                            <td className="text-right font-mono">
                              {line.quantity}
                              {line.free_qty > 0 && <span className="text-green-500 text-xs ml-1">(+{line.free_qty})</span>}
                            </td>
                            <td className="text-right font-mono">{parseFloat(line.unit_price || line.price || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 3})} {line.currency || sourceInvoice.currency || ''}</td>
                            <td className="text-right font-mono font-medium">{parseFloat(line.line_total || line.line_total_usd || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 3})}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t">
                          <td colSpan={4} className="text-right font-medium">Total ({sourceInvoice.currency || 'USD'})</td>
                          <td className="text-right font-mono font-bold">{parseFloat(sourceInvoice.total_usd || sourceInvoice.total || 0).toLocaleString(undefined, {minimumFractionDigits: 3})}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setViewVoucher(null); setSourceInvoice(null); }}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="w-5 h-5" />
              Delete Voucher
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete voucher <strong>{deleteConfirm?.voucher_number}</strong>?
              <br /><br />
              <span className="text-amber-400">
                Warning: This will also unpost the voucher and reverse all account balance changes.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)} disabled={deleting}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteVoucher} disabled={deleting}>
              {deleting ? 'Deleting...' : 'Delete Voucher'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LedgerDialog;
